import { doc, orderBy, setDoc } from 'firebase/firestore'
import { useState } from 'react'
import { useLocation as useRoute, useNavigate } from 'react-router'
import {
  CONTACT_TOPICS, CONTACT_TOPIC_IDS, FAQ_CATALOG, FAQ_GROUPS, FAQ_GROUP_LABELS, SECTIONS, contactCovers, contactLabel, contactPurpose, newId, slugify, splitLegacyContact,
  type ContactsSection, type FaqGroup, type FaqQuestion, type FaqSection as Doc, type PortalConfig,
} from '@sonder/shared'
import { useMode } from '@/app/mode'
import { useAuth } from '@/auth/AuthProvider'
import { useClient } from '@/data/client'
import { useSectionEditor, type SectionEditor } from '@/data/editor'
import { useCollection } from '@/data/hooks'
import { db } from '@/lib/firebase'
import { Button, CopyToDialog, EditButton, Field, Icons, Input, Segmented, Select, StatusPill, Toggle, useConfirm, useToast, type CopyTarget } from '@/ui'
import { BlockHead, NoEntities, useBrands, useLocations } from './common'
import { newContact, newContactId, type Contact, type ContactsNavState } from './contactHelpers'
import { FieldEditor } from './FieldEditor'

const F = Object.fromEntries(SECTIONS.faq.fields.map((f) => [f.key, f]))
/**
 * A question as this page lists it: a standard one from the shared catalog, or one asked only of this client.
 * `edited` marks a standard question this client sees in its own wording or group; `standardLabel` and
 * `standardGroup` are the shared ones.
 */
type Question = FaqQuestion & { custom: boolean; edited: boolean; standardLabel: string; standardGroup: FaqGroup }
/** Who an added or edited question applies to. */
type Scope = 'client' | 'all'
type QuestionDraft = { label: string; group: FaqGroup; scope: Scope }
/** The dropdown option that opens Contact information instead of assigning. */
const NEW_CONTACT = '__new-contact__'
const TOPIC_LABEL: Record<string, string> = Object.fromEntries(CONTACT_TOPICS.map((t) => [t.id, t.label]))

export function FaqSection({ clientId, config }: { clientId: string; config: PortalConfig }) {
  const ed = useSectionEditor<Doc>(clientId, 'faq')
  const contactsEd = useSectionEditor<ContactsSection>(clientId, 'contacts')
  const route = useRoute()
  const navigate = useNavigate()
  const catalog = useCollection<Omit<FaqQuestion, 'id'>>('faqQuestions', [orderBy('order')])
  const brands = useBrands(clientId)
  const locations = useLocations(clientId)
  const mode = useMode()
  const { can } = useAuth()
  const clientName = useClient(clientId).data?.name ?? 'this client'
  const confirm = useConfirm()
  const { toast } = useToast()
  // `${brandId}:${questionId}` while that row's wording is being edited.
  const [renaming, setRenaming] = useState<string | null>(null)
  // `${brandId}:${group}` while that group's "Add a question" row is open.
  const [adding, setAdding] = useState<string | null>(null)
  // An answer being copied to the same question elsewhere; `from` is the copy target it came from.
  const [copying, setCopying] = useState<{ q: Question; from: string; value: string } | null>(null)
  if (!ed.doc) return null
  if (brands.order.length === 0) return <NoEntities what="brands" hint="Answers are per brand; add brands first." />
  const standard = (catalog.items.length ? catalog.items.map((q) => ({ id: q.id, group: q.group, order: q.order, label: q.label, active: q.active })) : FAQ_CATALOG)
    .filter((q) => q.active && (!config.faqQuestionIds || config.faqQuestionIds.includes(q.id)))
  const questions: Question[] = [
    ...standard.map((q) => {
      const ownLabel = ed.doc?.labels?.[q.id]
      const ownGroup = ed.doc?.groups?.[q.id]
      return { ...q, label: ownLabel ?? q.label, group: ownGroup ?? q.group, custom: false, edited: Boolean(ownLabel || ownGroup), standardLabel: q.label, standardGroup: q.group }
    }),
    ...Object.entries(ed.doc.custom ?? {}).map(([id, q]) => ({ id, group: q.group, order: q.order, label: q.label, active: true, custom: true, edited: false, standardLabel: q.label, standardGroup: q.group })),
  ].sort((a, b) => a.order - b.order)
  // The team words questions, for this client or (with the settings permission) for every client.
  const editing = mode === 'prep'
  const canEdit = editing && can('clients.edit')
  const canAllClients = can('settings.manage')
  /** A new standard question in the shared catalog; returns its id. */
  const createStandard = (label: string, group: FaqGroup) => {
    // Slug of the wording, made unique if a question already uses it.
    const base = slugify(label).slice(0, 24) || 'question'
    const id = catalog.items.some((x) => x.id === base) || FAQ_CATALOG.some((x) => x.id === base) ? `${base}-${newId().slice(0, 4)}` : base
    const order = Math.max(0, ...catalog.items.filter((x) => x.group === group).map((x) => x.order)) + 1
    void setDoc(doc(db, `faqQuestions/${id}`), { label, group, order, active: true })
    return id
  }
  const addQuestion = ({ label, group, scope }: QuestionDraft) => {
    if (scope === 'all') createStandard(label, group)
    else ed.setRaw(`custom.custom-${slugify(label).slice(0, 24)}-${newId().slice(0, 4)}`, { label, group, order: 1000 + Object.keys(ed.doc?.custom ?? {}).length, createdAt: Date.now() })
    toast(scope === 'all' ? 'Question added for every client' : `Question added for ${clientName}`, 'success')
  }
  const saveQuestion = (q: Question, { label, group, scope }: QuestionDraft) => {
    if (q.custom && scope === 'client') {
      ed.setRaw(`custom.${q.id}.label`, label)
      ed.setRaw(`custom.${q.id}.group`, group)
    } else if (q.custom) {
      // Becomes standard: every client is asked it, and this client's answers move to the new question.
      const id = createStandard(label, group)
      for (const [b, qs] of Object.entries(ed.doc?.brands ?? {})) {
        if (!qs[q.id]) continue
        ed.setRaw(`brands.${b}.${id}`, qs[q.id])
        ed.remove(`brands.${b}.${q.id}`)
      }
      ed.remove(`custom.${q.id}`)
      toast('Now a standard question for every client', 'success')
    } else if (scope === 'client') {
      // Only this client sees the change; matching the standard again drops the override.
      if (label === q.standardLabel) ed.remove(`labels.${q.id}`)
      else ed.setRaw(`labels.${q.id}`, label)
      if (group === q.standardGroup) ed.remove(`groups.${q.id}`)
      else ed.setRaw(`groups.${q.id}`, group)
    } else {
      void setDoc(doc(db, `faqQuestions/${q.id}`), { label, group, order: q.order, active: q.active }, { merge: true })
      // This client now follows the standard question again.
      ed.remove(`labels.${q.id}`)
      ed.remove(`groups.${q.id}`)
    }
  }
  const deleteCustom = async (q: Question) => {
    if (!(await confirm({ title: 'Delete this question?', body: `“${q.label}” and its answers for every brand will be removed.`, confirmLabel: 'Delete' }))) return
    setRenaming(null)
    ed.remove(`custom.${q.id}`)
    for (const b of Object.keys(ed.doc?.brands ?? {})) if (ed.doc?.brands?.[b]?.[q.id]) ed.remove(`brands.${b}.${q.id}`)
  }
  const brandLocationsOf = (b: string) => locations.order.filter((l) => locations.items[l]?.brandId === b)
  const isSplit = (b: string, q: Question) => !CONTACT_TOPIC_IDS.includes(q.id) && Boolean(ed.doc?.brands?.[b]?.[q.id]?.split) && brandLocationsOf(b).length > 1
  // Everywhere else this question is answered: a brand, or each of its locations when its answer differs by location.
  const copyTargets = (q: Question): Array<CopyTarget & { path: string }> =>
    brands.order.flatMap((b) =>
      isSplit(b, q)
        ? brandLocationsOf(b).map((l) => ({ id: `loc:${l}`, label: locations.name(l), path: `brands.${b}.${q.id}.locations.${l}` }))
        : [{ id: `brand:${b}`, label: brands.name(b), path: `brands.${b}.${q.id}.answer` }],
    ).map((t) => ({ ...t, note: ed.getField<string>(t.path)?.value?.trim() ? 'Replaces its answer' : undefined }))
  /** "Copy to…" just under an answer's box; only once there's an answer and somewhere to copy it. */
  const copyAction = (q: Question, from: string, path: string) => {
    const value = ed.getField<string>(path)?.value ?? ''
    if (!value.trim() || !copyTargets(q).some((t) => t.id !== from)) return undefined
    return (
      <button
        type="button"
        onClick={() => setCopying({ q, from, value })}
        className="inline-flex items-center gap-1.5 text-[0.6875rem] font-medium uppercase tracking-eyebrow text-dim hover:text-ink"
      >
        <Icons.Copy size={12} /> Copy to…
      </button>
    )
  }
  // Inside a brand's block its name is already the heading, so "Saints + Council · Colony Square" reads as "Colony Square".
  const shortLocation = (l: string, brandId: string) => {
    const name = locations.name(l)
    const prefix = `${brands.name(brandId)} · `
    return name.startsWith(prefix) ? name.slice(prefix.length) : name
  }

  const contactsById = contactsEd.doc?.items ?? {}
  const contacts = Object.entries(contactsById).sort(([, a], [, b]) => a.createdAt - b.createdAt)
  // Contacts is its own step; these open it on the right card.
  const contactsHref = route.pathname.replace(/\/onboarding\/[^/]+$/, '/onboarding/contacts')
  const openContact = (id: string) => navigate(contactsHref, { state: { focusContact: id } satisfies ContactsNavState })
  const openGroup = (group: string) => navigate(contactsHref, { state: { focusGroup: group } satisfies ContactsNavState })
  const addContacts = (inits: Parameters<typeof newContact>[0][]) => {
    const ids = inits.map((init) => {
      const id = newContactId()
      contactsEd.setRaw(`items.${id}`, newContact(init, mode))
      return id
    })
    if (ids[0]) openContact(ids[0])
  }

  return (
    <div className="grid gap-12">
      <CopyToDialog
        open={copying !== null}
        onClose={() => setCopying(null)}
        what="this answer"
        targets={copying ? copyTargets(copying.q).filter((t) => t.id !== copying.from) : []}
        onCopy={(ids) => {
          if (!copying) return
          const targets = copyTargets(copying.q).filter((t) => ids.includes(t.id))
          for (const t of targets) ed.setValue(t.path, copying.value)
          toast(`Copied to ${targets.length === 1 ? targets[0]!.label : `${targets.length} places`}`, 'success')
          setCopying(null)
        }}
      />
      {brands.order.map((brandId) => {
        const brandLocations = locations.order.filter((l) => locations.items[l]?.brandId === brandId)
        return (
          <section key={brandId} aria-label={brands.name(brandId)} className="grid gap-8 border-t border-ink pt-6">
            <BlockHead eyebrow="FAQ" title={brands.name(brandId)} />
            {FAQ_GROUPS.map((g) => {
              const qs = questions.filter((q) => q.group === g)
              if (!qs.length) return null
              return (
                <div key={g} className="grid gap-2">
                  <p className="text-eyebrow uppercase tracking-eyebrow font-medium text-ink">{FAQ_GROUP_LABELS[g]}</p>
                  <div className="divide-y divide-gray-200 border-y border-gray-200">
                    {qs.map((q) => {
                      const base = `brands.${brandId}.${q.id}`
                      const routed = CONTACT_TOPIC_IDS.includes(q.id)
                      // Chosen explicitly per question; contacts tagged for the topic are only suggested.
                      const assignedIds = (ed.doc?.brands?.[brandId]?.[q.id]?.contacts ?? []).filter((id) => contactsById[id])
                      const assigned = assignedIds.map((id): [string, Contact] => [id, contactsById[id]!])
                      const pool = contacts.filter(([id, c]) => !assignedIds.includes(id) && brandLocations.some((l) => contactCovers(c, l)))
                      const suggested = routed ? pool.filter(([, c]) => c.topics?.includes(q.id)) : []
                      const assign = (ids: string[]) => ed.setRaw(`${base}.contacts`, ids)
                      const split = !routed && Boolean(ed.doc?.brands?.[brandId]?.[q.id]?.split) && brandLocations.length > 1
                      const answered =
                        assigned.length > 0 ||
                        (split ? brandLocations.some((l) => ed.getField(`${base}.locations.${l}`)?.value) : Boolean(ed.getField(`${base}.answer`)?.value))
                      const rowKey = `${brandId}:${q.id}`
                      if (renaming === rowKey) {
                        return (
                          <div key={q.id} className="max-w-form py-3">
                            <QuestionForm
                              initial={{ label: q.label, group: q.group, scope: 'client' }}
                              custom={q.custom}
                              canAllClients={canAllClients}
                              submitLabel="Update question"
                              onSubmit={(d) => saveQuestion(q, d)}
                              onCancel={() => setRenaming(null)}
                              extra={
                                q.custom ? (
                                  <Button variant="danger-link" size="sm" arrow={false} className="ml-auto" onClick={() => void deleteCustom(q)}>
                                    Delete question
                                  </Button>
                                ) : (
                                  q.edited && (
                                    <Button
                                      variant="link"
                                      size="sm"
                                      arrow={false}
                                      className="ml-auto"
                                      title={`Standard: “${q.standardLabel}” in ${FAQ_GROUP_LABELS[q.standardGroup]}`}
                                      onClick={() => {
                                        ed.remove(`labels.${q.id}`)
                                        ed.remove(`groups.${q.id}`)
                                        setRenaming(null)
                                      }}
                                    >
                                      Reset to standard
                                    </Button>
                                  )
                                )
                              }
                            />
                          </div>
                        )
                      }
                      return (
                        // Sharing a name makes the browser keep only one question open across the whole page.
                        <details
                          key={q.id}
                          name="faq-question"
                          className="group"
                          // Opening another question means moving on, so drop a wording edit left open elsewhere.
                          onToggle={(e) => e.currentTarget.open && setRenaming(null)}
                        >
                          <summary className="flex cursor-pointer list-none items-center gap-4 py-4 text-body text-ink marker:hidden group-open:font-medium">
                            {/* On a phone the question gets the row's full width and its tags go on a line beneath; from sm up they sit to its right. */}
                            <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1.5">
                              <span className="flex basis-full items-center gap-2 sm:flex-1 sm:basis-auto">
                                <span>{q.label}</span>
                                {canEdit && (
                                  <EditButton
                                    // Out of the way until the row is hovered or open (or the button is reached by keyboard).
                                    className="opacity-0 group-open:opacity-100 group-hover:opacity-100"
                                    label={`Edit question “${q.label}”`}
                                    onClick={(e) => {
                                      // Edit the wording without opening or closing the question.
                                      e.preventDefault()
                                      setRenaming(rowKey)
                                    }}
                                  />
                                )}
                              </span>
                              {/* Right-aligned beside the status so every client-only question shows its tag in the same spot. */}
                              {editing && (q.custom || q.edited) && (
                                <StatusPill tone="ink" dot={false} size="xs">
                                  {q.custom ? 'This client only' : 'Edited for this client'}
                                </StatusPill>
                              )}
                              <StatusPill tone={answered ? 'ok' : 'neutral'} className="max-sm:px-1.5 max-sm:py-px max-sm:text-[0.625rem]">
                                {answered ? 'Answered' : 'Open'}
                              </StatusPill>
                            </span>
                            <span aria-hidden="true" className="text-dim transition-transform group-open:rotate-45">+</span>
                          </summary>
                          {/* Indented under a rule so everything here reads as part of the question above. */}
                          <div className="mb-6 grid max-w-form gap-6 border-l border-gray-300 pl-5">
                            {routed && (
                              <div className="grid gap-3">
                                <p className="text-eyebrow uppercase tracking-eyebrow font-medium text-ink">Who handles this</p>
                                {assigned.length === 0 ? (
                                  <p className="text-small text-dim">No one assigned yet.</p>
                                ) : (
                                  <HandlerList
                                    handlers={assigned}
                                    topic={q.id}
                                    where={(c) => whereText(c, brandLocations, locations.name)}
                                    onEdit={openContact}
                                    onRemove={(id) => assign(assignedIds.filter((x) => x !== id))}
                                  />
                                )}
                                {suggested.length > 0 && (
                                  <Suggestions
                                    topic={TOPIC_LABEL[q.id] ?? q.label}
                                    contacts={suggested}
                                    where={(c) => whereText(c, brandLocations, locations.name)}
                                    onAssign={(ids) => assign([...assignedIds, ...ids])}
                                  />
                                )}
                                <LegacyNote ed={ed} qid={q.id} onMove={(inits) => addContacts(inits.map((p) => ({ ...p, topics: [q.id] })))} />
                                {pool.length === 0 ? (
                                  // Nobody left to pick, so the dropdown's only option becomes the action itself (it opens Contact information).
                                  // Styled like the hours builder's "Add time block": a quiet action inside an open row, level with Edit and Unassign.
                                  <button
                                    type="button"
                                    onClick={() => openGroup(brandId)}
                                    className="inline-flex min-h-8 items-center gap-1 justify-self-start text-[0.6875rem] uppercase tracking-eyebrow text-dim hover:text-ink"
                                  >
                                    <Icons.Plus size={11} /> Add a new contact
                                  </button>
                                ) : (
                                <div>
                                  <Select<string>
                                    size="sm"
                                    className="w-full sm:w-auto sm:min-w-72"
                                    aria-label={`Assign a contact to “${q.label}”`}
                                    placeholder="Assign a contact…"
                                    value=""
                                    // The last option leaves for Contact information (this brand's group) to create one.
                                    onValueChange={(id) => (id === NEW_CONTACT ? openGroup(brandId) : assign([...assignedIds, id]))}
                                    options={[
                                      ...pool.map(([id, c]) => ({ value: id, label: <ContactOption c={c} where={whereText(c, brandLocations, locations.name)} /> })),
                                      {
                                        value: NEW_CONTACT,
                                        divider: true,
                                        label: (
                                          <span className="inline-flex items-center gap-2">
                                            <Icons.Plus size={11} /> Add a new contact
                                          </span>
                                        ),
                                      },
                                    ]}
                                  />
                                </div>
                                )}
                              </div>
                            )}
                            {routed ? (
                              <FieldEditor
                                editor={ed}
                                path={`${base}.answer`}
                                def={{ ...F.answer!, label: 'Anything to add' }}
                                rows={2}
                                hideOptional
                                action={copyAction(q, `brand:${brandId}`, `${base}.answer`)}
                              />
                            ) : (
                              <div className="grid gap-4">
                                {/* The answer, then (when it differs by location) one box per location, each with its own Copy to…. */}
                                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                                  <span className="text-eyebrow uppercase tracking-eyebrow font-medium text-ink">Answer</span>
                                  {brandLocations.length > 1 && (
                                    <Toggle
                                      size="sm"
                                      checked={split}
                                      onChange={(v) => {
                                        // Start each location from the shared answer so nothing has to be retyped.
                                        const shared = ed.getField<string>(`${base}.answer`)?.value ?? ''
                                        if (v && shared) for (const l of brandLocations) if (!ed.getField(`${base}.locations.${l}`)?.value) ed.setValue(`${base}.locations.${l}`, shared)
                                        ed.setRaw(`${base}.split`, v)
                                      }}
                                      label="Differs by location"
                                    />
                                  )}
                                </div>
                                {split ? (
                                  brandLocations.map((l) => (
                                    <FieldEditor
                                      key={l}
                                      editor={ed}
                                      path={`${base}.locations.${l}`}
                                      def={F.answer!}
                                      label={shortLocation(l, brandId)}
                                      rows={2}
                                      hideOptional
                                      action={copyAction(q, `loc:${l}`, `${base}.locations.${l}`)}
                                    />
                                  ))
                                ) : (
                                  <FieldEditor editor={ed} path={`${base}.answer`} def={F.answer!} rows={2} labelHidden hideOptional action={copyAction(q, `brand:${brandId}`, `${base}.answer`)} />
                                )}
                              </div>
                            )}
                          </div>
                        </details>
                      )
                    })}
                  </div>
                  {/* Team only: add a question to this group right where it's needed. It's asked of every brand. */}
                  {canEdit &&
                    (adding === `${brandId}:${g}` ? (
                      <div className="max-w-form pt-2">
                        <QuestionForm
                          initial={{ label: '', group: g, scope: 'client' }}
                          custom
                          canAllClients={canAllClients}
                          submitLabel="Add question"
                          onSubmit={addQuestion}
                          onCancel={() => setAdding(null)}
                        />
                      </div>
                    ) : (
                      <div className="pt-2">
                        <Button variant="ghost" size="sm" arrow={false} icon={<Icons.Plus size={12} />} onClick={() => setAdding(`${brandId}:${g}`)}>
                          Add a question
                        </Button>
                      </div>
                    ))}
                </div>
              )
            })}
          </section>
        )
      })}
    </div>
  )
}

/** Which of this brand's locations a contact covers, for the line under it. */
function whereText(c: Contact, brandLocations: string[], name: (id: string) => string): string {
  const ids = c.locationIds?.length ? brandLocations.filter((l) => c.locationIds.includes(l)) : []
  if (ids.length) return ids.map(name).join(', ')
  return brandLocations.length > 1 ? 'Every location' : ''
}

/** The contacts assigned to a question: who or where first, then how to reach them, then edit and remove. */
function HandlerList({ handlers, topic, where, onEdit, onRemove }: { handlers: Array<[string, Contact]>; topic: string; where: (c: Contact) => string; onEdit: (id: string) => void; onRemove: (id: string) => void }) {
  return (
    <ul className="divide-y divide-gray-200 border-y border-gray-200">
      {handlers.map(([id, c]) => {
        const person = Boolean(c.name?.value?.trim())
        const place = where(c)
        // The purpose only adds something when the contact handles more than this question.
        const purpose = c.topics?.length === 1 && c.topics[0] === topic ? '' : contactPurpose(c)
        const primary = person ? contactLabel(c) : place || contactLabel(c)
        const secondary = person ? place : place ? purpose : ''
        return (
          <li key={id} className="flex flex-wrap items-center gap-x-6 gap-y-1 py-3 text-small">
            <div className="min-w-0 flex-1">
              <p className="text-ink">{primary}</p>
              {secondary && <p className="text-[0.8125rem] text-dim">{secondary}</p>}
            </div>
            <div className="grid text-charcoal tabular-nums sm:justify-items-end">
              {c.phone?.value && <a href={`tel:${c.phone.value.replace(/[^\d+]/g, '')}`} className="hover:text-ink">{c.phone.value}</a>}
              {c.email?.value && <a href={`mailto:${c.email.value}`} className="hover:text-ink">{c.email.value}</a>}
              {!c.phone?.value && !c.email?.value && <span className="text-dim">No number or email yet</span>}
            </div>
            <div className="flex items-center gap-4">
              <button type="button" onClick={() => onEdit(id)} className="text-eyebrow uppercase tracking-eyebrow text-dim underline-offset-4 hover:text-ink hover:underline">
                Edit
              </button>
              <button type="button" onClick={() => onRemove(id)} aria-label={`Unassign ${contactLabel(c)}`} className="text-eyebrow uppercase tracking-eyebrow text-dim underline-offset-4 hover:text-danger hover:underline">
                Unassign
              </button>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

/**
 * Team only: a question's wording, its group and who it applies to, for adding one or editing it in place.
 * `custom` says whether it starts as this client's own question (always true when adding).
 */
function QuestionForm({ initial, custom, canAllClients, submitLabel, onSubmit, onCancel, extra }: {
  initial: QuestionDraft
  custom: boolean
  canAllClients: boolean
  submitLabel: string
  onSubmit: (d: QuestionDraft) => void
  onCancel: () => void
  extra?: React.ReactNode
}) {
  const [d, setD] = useState(initial)
  const submit = () => {
    const label = d.label.trim()
    if (!label) return
    onSubmit({ ...d, label })
    onCancel()
  }
  const note =
    d.scope === 'all'
      ? custom
        ? 'Every client will be asked this. Answers already given here stay attached.'
        : 'Changes the standard question for every client. Answers stay attached.'
      : custom
        ? 'Only this client is asked this.'
        : 'Only this client sees the change; everyone else keeps the standard question.'
  return (
    <div className="grid gap-4">
      <Field label="Question" labelHidden>
        <Input
          autoFocus
          value={d.label}
          placeholder="Is there a coat check?"
          onChange={(e) => setD({ ...d, label: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submit()
            }
            if (e.key === 'Escape') onCancel()
          }}
        />
      </Field>
      <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
        <Field label="Group" className="w-48">
          <Select<FaqGroup> size="sm" value={d.group} onValueChange={(group) => setD({ ...d, group })} options={FAQ_GROUPS.map((g) => ({ value: g, label: FAQ_GROUP_LABELS[g] }))} />
        </Field>
        {canAllClients && (
          <div className="grid gap-2">
            <span className="text-eyebrow uppercase tracking-eyebrow font-medium text-ink">Applies to</span>
            <Segmented<Scope>
              label="Applies to"
              value={d.scope}
              onChange={(scope) => setD({ ...d, scope })}
              options={[
                { value: 'client', label: 'This client only' },
                { value: 'all', label: 'All clients' },
              ]}
            />
          </div>
        )}
      </div>
      <p className="text-small text-dim">{note}</p>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Button size="sm" arrow={false} onClick={submit} disabled={!d.label.trim()}>
          {submitLabel}
        </Button>
        <Button variant="link" size="sm" arrow={false} onClick={onCancel}>
          Cancel
        </Button>
        {extra}
      </div>
    </div>
  )
}

/**
 * A contact as the assign dropdown lists it: who (the person's name, or what the line is for when nobody is named),
 * then what it's for, where and how to reach it, so similar lines can be told apart.
 */
function ContactOption({ c, where }: { c: Contact; where: string }) {
  const named = Boolean(c.name?.value?.trim())
  const details = [named ? contactPurpose(c) : '', where, c.phone?.value || c.email?.value || ''].filter(Boolean)
  return (
    <span className="grid gap-0.5 py-1">
      <span className="font-medium">{contactLabel(c)}</span>
      {details.length > 0 && <span className="text-[0.75rem] opacity-70">{details.join(' · ')}</span>}
    </span>
  )
}

/** Contacts tagged for this topic on Contact information but not assigned here yet; one tap assigns. */
function Suggestions({ topic, contacts, where, onAssign }: { topic: string; contacts: Array<[string, Contact]>; where: (c: Contact) => string; onAssign: (ids: string[]) => void }) {
  return (
    <div className="grid gap-2 border-l-2 border-ink bg-info-wash px-4 py-3 text-small">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <p className="text-charcoal">Suggested: tagged “{topic}” in Contact information</p>
        {contacts.length > 1 && (
          <button type="button" onClick={() => onAssign(contacts.map(([id]) => id))} className="text-ink underline underline-offset-4">
            Assign all
          </button>
        )}
      </div>
      <ul className="grid gap-1.5">
        {contacts.map(([id, c]) => (
          <li key={id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
            <span className="text-ink">{[contactLabel(c), where(c)].filter(Boolean).join(' · ')}</span>
            {c.phone?.value && <span className="text-charcoal tabular-nums">{c.phone.value}</span>}
            <button type="button" onClick={() => onAssign([id])} className="ml-auto text-ink underline underline-offset-4">
              Assign
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Free-text routing saved before Contacts existed, with a one-click move into real contacts. */
function LegacyNote({ ed, qid, onMove }: { ed: SectionEditor<Doc>; qid: string; onMove: (inits: ReturnType<typeof splitLegacyContact>) => void }) {
  const name = ed.getField<string>(`answers.${qid}.contact`)?.value?.trim() ?? ''
  const email = ed.getField<string>(`answers.${qid}.email`)?.value?.trim() ?? ''
  if (!name && !email) return null
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-l-2 border-warn bg-warn-wash px-3 py-2 text-small">
      <span className="text-dim">Previously noted:</span>
      <span className="text-ink">{[name, email].filter(Boolean).join(' · ')}</span>
      <button
        type="button"
        onClick={() => {
          // One contact per address in the note; Contacts opens on the first.
          onMove(splitLegacyContact(name, email))
          ed.remove(`answers.${qid}.contact`)
          ed.remove(`answers.${qid}.email`)
        }}
        className="ml-auto text-ink underline underline-offset-4"
      >
        Move to Contact information
      </button>
    </div>
  )
}
